import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMDXComponents as _provideComponents } from "@mdx-js/react";
import { RuleItem } from './index';
import { Accordion } from '@skillrecordings/ui';
function _createMdxContent(props) {
    const _components = Object.assign({
        p: "p",
        code: "code",
        pre: "pre",
        div: "div",
        a: "a",
        h2: "h2",
        em: "em"
    }, _provideComponents(), props.components);
    return _jsxs(Accordion, {
        type: "multiple",
        className: "w-full flex flex-col space-y-2 [&>h2]:pt-16 [&>h2]:pb-5 [&>h2]:text-3xl [&>h2]:font-heading",
        children: [
            _jsxs(RuleItem, {
                title: 'Make `JSON.parse` return `unknown`',
                children: [
                    _jsxs(_components.p, {
                        children: [
                            _jsx(_components.code, {
                                children: "JSON.parse"
                            }),
                            " returning ",
                            _jsx(_components.code, {
                                children: "any"
                            }),
                            " can cause nasty, subtle bugs. Frankly, any ",
                            _jsx(_components.code, {
                                children: "any"
                            }),
                            "'s can cause bugs because they disable typechecking on the values they describe."
                        ]
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "import '@total-typescript/ts-reset/json-parse'\n"
                        })
                    }),
                    _jsxs("div", {
                        children: [
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// BEFORE\nconst result = JSON.parse('{}') // any\n"
                                })
                            }),
                            _jsxs(_components.p, {
                                children: [
                                    "By changing the result of ",
                                    _jsx(_components.code, {
                                        children: "JSON.parse"
                                    }),
                                    " to ",
                                    _jsx(_components.code, {
                                        children: "unknown"
                                    }),
                                    ", we're now forced to either validate the ",
                                    _jsx(_components.code, {
                                        children: "unknown"
                                    }),
                                    " to ensure it's the correct type (perhaps using ",
                                    _jsx(_components.a, {
                                        href: "https://github.com/colinhacks/zod",
                                        children: _jsx(_components.code, {
                                            children: "zod"
                                        })
                                    }),
                                    "), or cast it with ",
                                    _jsx(_components.code, {
                                        children: "as"
                                    }),
                                    "."
                                ]
                            }),
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// AFTER\nimport '@total-typescript/ts-reset/json-parse'\n\nconst result = JSON.parse('{}') // unknown\n"
                                })
                            })
                        ]
                    })
                ]
            }),
            _jsxs(RuleItem, {
                title: 'Make `.json()` return `unknown`',
                children: [
                    _jsxs(_components.p, {
                        children: [
                            "Just like ",
                            _jsx(_components.code, {
                                children: "JSON.parse"
                            }),
                            ", ",
                            _jsx(_components.code, {
                                children: ".json()"
                            }),
                            " returning ",
                            _jsx(_components.code, {
                                children: "any"
                            }),
                            " introduces unwanted ",
                            _jsx(_components.code, {
                                children: "any"
                            }),
                            "'s into your application code."
                        ]
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "import '@total-typescript/ts-reset/fetch'\n"
                        })
                    }),
                    _jsxs("div", {
                        children: [
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// BEFORE\nfetch('/')\n  .then((res) => res.json())\n  .then((json) => {\n    console.log(json) // any\n  })\n"
                                })
                            }),
                            _jsxs(_components.p, {
                                children: [
                                    "By forcing ",
                                    _jsx(_components.code, {
                                        children: "res.json"
                                    }),
                                    " to return ",
                                    _jsx(_components.code, {
                                        children: "unknown"
                                    }),
                                    ", we're encouraged to distrust its results, making us more likely to validate the results of ",
                                    _jsx(_components.code, {
                                        children: "fetch"
                                    }),
                                    "."
                                ]
                            }),
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// AFTER\nimport '@total-typescript/ts-reset/fetch'\n\nfetch('/')\n  .then((res) => res.json())\n  .then((json) => {\n    console.log(json) // unknown\n  })\n"
                                })
                            })
                        ]
                    })
                ]
            }),
            _jsxs(RuleItem, {
                title: "Make `.filter(Boolean)` filter out falsy values",
                children: [
                    _jsxs(_components.p, {
                        children: [
                            "The default behaviour of ",
                            _jsx(_components.code, {
                                children: ".filter"
                            }),
                            " can feel pretty frustrating. Given the code below:"
                        ]
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "import '@total-typescript/ts-reset/filter-boolean'\n"
                        })
                    }),
                    _jsxs("div", {
                        children: [
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// BEFORE\nconst filteredArray = [1, 2, undefined].filter(Boolean) // (number | undefined)[]\n"
                                })
                            }),
                            _jsxs(_components.p, {
                                children: [
                                    "It feels natural that TypeScript should understand that you've filtered out the ",
                                    _jsx(_components.code, {
                                        children: "undefined"
                                    }),
                                    " from ",
                                    _jsx(_components.code, {
                                        children: "filteredArray"
                                    }),
                                    ". You can make this work, but you need to mark it as a type predicate:"
                                ]
                            }),
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "const filteredArray = [1, 2, undefined].filter((item): item is number => {\n  return !!item\n}) // number[]\n"
                                })
                            }),
                            _jsxs(_components.p, {
                                children: [
                                    "Using ",
                                    _jsx(_components.code, {
                                        children: ".filter(Boolean)"
                                    }),
                                    " is a really common shorthand for this. So, this rule makes it so ",
                                    _jsx(_components.code, {
                                        children: ".filter(Boolean)"
                                    }),
                                    " acts like a type predicate on the array passed in, removing any falsy values from the array member."
                                ]
                            }),
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// AFTER\nimport '@total-typescript/ts-reset/filter-boolean'\n\nconst filteredArray = [1, 2, undefined].filter(Boolean) // number[]\n"
                                })
                            })
                        ]
                    })
                ]
            }),
            _jsxs(RuleItem, {
                title: "Make `.includes` on `as const` arrays less strict",
                children: [
                    _jsxs(_components.p, {
                        children: [
                            "This rule improves on TypeScript's default ",
                            _jsx(_components.code, {
                                children: ".includes"
                            }),
                            " behaviour. Without this rule enabled, the argument passed to ",
                            _jsx(_components.code, {
                                children: ".includes"
                            }),
                            " MUST be a member of the array it's being tested against."
                        ]
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "import '@total-typescript/ts-reset/array-includes'\n"
                        })
                    }),
                    _jsxs("div", {
                        children: [
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// BEFORE\nconst users = ['matt', 'sofia', 'waqas'] as const\n\n// Argument of type '\"bryan\"' is not assignable to\n// parameter of type '\"matt\" | \"sofia\" | \"waqas\"'.\nusers.includes('bryan')\n"
                                })
                            }),
                            _jsxs(_components.p, {
                                children: [
                                    "This can often feel extremely awkward. But with the rule enabled, ",
                                    _jsx(_components.code, {
                                        children: ".includes"
                                    }),
                                    " now takes a widened version of the literals in the ",
                                    _jsx(_components.code, {
                                        children: "const"
                                    }),
                                    " array."
                                ]
                            }),
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// AFTER\nimport '@total-typescript/ts-reset/array-includes'\n\nconst users = ['matt', 'sofia', 'waqas'] as const\n\n// .includes now takes a string as the first parameter\nusers.includes('bryan')\n"
                                })
                            }),
                            _jsx(_components.p, {
                                children: "This means you can test non-members of the array safely."
                            })
                        ]
                    })
                ]
            }),
            _jsxs(RuleItem, {
                title: "Make `.indexOf` on `as const` arrays less strict",
                children: [
                    _jsxs(_components.p, {
                        children: [
                            "Exactly the same behaviour of ",
                            _jsx(_components.code, {
                                children: ".includes"
                            }),
                            " (explained above), but for ",
                            _jsx(_components.code, {
                                children: ".lastIndexOf"
                            }),
                            " and ",
                            _jsx(_components.code, {
                                children: ".indexOf"
                            }),
                            "."
                        ]
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "import '@total-typescript/ts-reset/array-index-of'\n"
                        })
                    })
                ]
            }),
            _jsxs(RuleItem, {
                title: "Make `Set.has()` less strict",
                children: [
                    _jsxs(_components.p, {
                        children: [
                            "Similar to ",
                            _jsx(_components.code, {
                                children: ".includes"
                            }),
                            ", ",
                            _jsx(_components.code, {
                                children: "Set.has()"
                            }),
                            " doesn't let you pass members that don't exist in the set:"
                        ]
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "import '@total-typescript/ts-reset/set-has'\n"
                        })
                    }),
                    _jsxs("div", {
                        children: [
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// BEFORE\nconst userSet = new Set(['matt', 'sofia', 'waqas'] as const)\n\n// Argument of type '\"bryan\"' is not assignable to\n// parameter of type '\"matt\" | \"sofia\" | \"waqas\"'.\nuserSet.has('bryan')\n"
                                })
                            }),
                            _jsxs(_components.p, {
                                children: [
                                    "With the rule enabled, ",
                                    _jsx(_components.code, {
                                        children: "Set"
                                    }),
                                    " is much smarter:"
                                ]
                            }),
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// AFTER\nimport '@total-typescript/ts-reset/set-has'\n\nconst userSet = new Set(['matt', 'sofia', 'waqas'] as const)\n\n// .has now takes a string as the argument!\nuserSet.has('bryan')\n"
                                })
                            })
                        ]
                    })
                ]
            }),
            _jsxs(RuleItem, {
                title: "Make `Map.has()` less strict",
                children: [
                    _jsxs(_components.p, {
                        children: [
                            "Similar to ",
                            _jsx(_components.code, {
                                children: ".includes"
                            }),
                            " or ",
                            _jsx(_components.code, {
                                children: "Set.has()"
                            }),
                            ", ",
                            _jsx(_components.code, {
                                children: "Map.has()"
                            }),
                            " doesn't let you pass members that don't exist in the map's keys:"
                        ]
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "import '@total-typescript/ts-reset/map-has'\n"
                        })
                    }),
                    _jsxs("div", {
                        children: [
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// BEFORE\nconst userMap = new Map([\n  ['matt', 0],\n  ['sofia', 1],\n  ['waqas', 2],\n] as const)\n\n// Argument of type '\"bryan\"' is not assignable to\n// parameter of type '\"matt\" | \"sofia\" | \"waqas\"'.\nuserMap.has('bryan')\n"
                                })
                            }),
                            _jsxs(_components.p, {
                                children: [
                                    "With the rule enabled, ",
                                    _jsx(_components.code, {
                                        children: "Map"
                                    }),
                                    " follows the same semantics as ",
                                    _jsx(_components.code, {
                                        children: "Set"
                                    }),
                                    "."
                                ]
                            }),
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// AFTER\nimport '@total-typescript/ts-reset/map-has'\n\nconst userMap = new Map([\n  ['matt', 0],\n  ['sofia', 1],\n  ['waqas', 2],\n] as const)\n\n// .has now takes a string as the argument!\nuserMap.has('bryan')\n"
                                })
                            })
                        ]
                    })
                ]
            }),
            _jsxs(RuleItem, {
                title: "Removing `any[]` from `Array.isArray()`",
                children: [
                    _jsxs(_components.p, {
                        children: [
                            "When you're using ",
                            _jsx(_components.code, {
                                children: "Array.isArray"
                            }),
                            ", you can introduce subtle ",
                            _jsx(_components.code, {
                                children: "any"
                            }),
                            "'s into your app's code."
                        ]
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "import '@total-typescript/ts-reset/is-array'\n"
                        })
                    }),
                    _jsxs("div", {
                        children: [
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// BEFORE\n\nconst validate = (input: unknown) => {\n  if (Array.isArray(input)) {\n    console.log(input) // any[]\n  }\n}\n"
                                })
                            }),
                            _jsxs(_components.p, {
                                children: [
                                    "With ",
                                    _jsx(_components.code, {
                                        children: "is-array"
                                    }),
                                    " enabled, this check will now mark the value as ",
                                    _jsx(_components.code, {
                                        children: "unknown[]"
                                    }),
                                    ":"
                                ]
                            }),
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// AFTER\nimport '@total-typescript/ts-reset/is-array'\n\nconst validate = (input: unknown) => {\n  if (Array.isArray(input)) {\n    console.log(input) // unknown[]\n  }\n}\n"
                                })
                            })
                        ]
                    })
                ]
            }),
            _jsxs(RuleItem, {
                title: "Making `sessionStorage` and `localStorage` safer",
                children: [
                    _jsxs(_components.p, {
                        children: [
                            "By default, ",
                            _jsx(_components.code, {
                                children: "localStorage"
                            }),
                            " and ",
                            _jsx(_components.code, {
                                children: "sessionStorage"
                            }),
                            " let you access any key, and return ",
                            _jsx(_components.code, {
                                children: "any"
                            }),
                            ":"
                        ]
                    }),
                    _jsxs("div", {
                        children: [
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// BEFORE\n\n// No error!\nlocalStorage.a.b.c.ohDear // any\n"
                                })
                            }),
                            _jsxs(_components.p, {
                                children: [
                                    "With this rule enabled, these keys now get typed as ",
                                    _jsx(_components.code, {
                                        children: "unknown"
                                    }),
                                    ":"
                                ]
                            }),
                            _jsx(_components.pre, {
                                children: _jsx(_components.code, {
                                    className: "language-ts",
                                    children: "// AFTER\nimport '@total-typescript/ts-reset/storage'\n\n// Error!\nlocalStorage.a.b.c.ohDear\n"
                                })
                            })
                        ]
                    })
                ]
            }),
            _jsx(_components.h2, {
                children: "Rules we won't add"
            }),
            _jsxs(RuleItem, {
                title: "`Object.keys`/`Object.entries`",
                children: [
                    _jsxs(_components.p, {
                        children: [
                            "A common ask is to provide 'better' typings for ",
                            _jsx(_components.code, {
                                children: "Object.keys"
                            }),
                            ", so that it returns ",
                            _jsx(_components.code, {
                                children: "Array<keyof T>"
                            }),
                            " instead of ",
                            _jsx(_components.code, {
                                children: "Array<string>"
                            }),
                            ". Same for ",
                            _jsx(_components.code, {
                                children: "Object.entries"
                            }),
                            ". ",
                            _jsx(_components.code, {
                                children: "ts-reset"
                            }),
                            " won't be including rules to change this."
                        ]
                    }),
                    _jsx(_components.p, {
                        children: "TypeScript is a structural typing system. One of the effects of this is that TypeScript can't always guarantee that your object types don't contain excess properties:"
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "type Func = () => {\n  id: string\n}\n\nconst func: Func = () => {\n  return {\n    id: '123',\n    // No error on an excess property!\n    name: 'Hello!',\n  }\n}\n"
                        })
                    }),
                    _jsxs(_components.p, {
                        children: [
                            "So, the only reasonable type for ",
                            _jsx(_components.code, {
                                children: "Object.keys"
                            }),
                            " to return is ",
                            _jsx(_components.code, {
                                children: "Array<string>"
                            }),
                            "."
                        ]
                    })
                ]
            }),
            _jsxs(RuleItem, {
                title: "Generics for `JSON.parse`, `Response.json` etc",
                children: [
                    _jsxs(_components.p, {
                        children: [
                            "A common request is for ",
                            _jsx(_components.code, {
                                children: "ts-reset"
                            }),
                            " to add type arguments to functions like ",
                            _jsx(_components.code, {
                                children: "JSON.parse"
                            }),
                            ":"
                        ]
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "const str = JSON.parse<string>('\"hello\"')\n\nconsole.log(str) // string\n"
                        })
                    }),
                    _jsxs(_components.p, {
                        children: [
                            "This appears to improve the DX by giving you autocomplete on the thing that gets returned from ",
                            _jsx(_components.code, {
                                children: "JSON.parse"
                            }),
                            "."
                        ]
                    }),
                    _jsx(_components.p, {
                        children: "However, we argue that this is a lie to the compiler and so, unsafe."
                    }),
                    _jsxs(_components.p, {
                        children: [
                            _jsx(_components.code, {
                                children: "JSON.parse"
                            }),
                            " and ",
                            _jsx(_components.code, {
                                children: "fetch"
                            }),
                            " represent ",
                            _jsx(_components.em, {
                                children: "validation boundaries"
                            }),
                            " - places where unknown data can enter your application code."
                        ]
                    }),
                    _jsxs(_components.p, {
                        children: [
                            "If you ",
                            _jsx(_components.em, {
                                children: "really"
                            }),
                            " know what data is coming back from a ",
                            _jsx(_components.code, {
                                children: "JSON.parse"
                            }),
                            ", then an ",
                            _jsx(_components.code, {
                                children: "as"
                            }),
                            " assertion feels like the right call:"
                        ]
                    }),
                    _jsx(_components.pre, {
                        children: _jsx(_components.code, {
                            className: "language-ts",
                            children: "const str = JSON.parse('\"hello\"') as string\n\nconsole.log(str) // string\n"
                        })
                    }),
                    _jsxs(_components.p, {
                        children: [
                            "This provides the types you intend and also signals to the developer that this is ",
                            _jsx(_components.em, {
                                children: "slightly"
                            }),
                            " unsafe."
                        ]
                    })
                ]
            })
        ]
    });
}
function MDXContent(props = {}) {
    const { wrapper: MDXLayout } = Object.assign({}, _provideComponents(), props.components);
    return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
        children: _jsx(_createMdxContent, props)
    })) : _createMdxContent(props);
}
export default MDXContent;
